<template>
  <span
    class="base-icon"
    :class="{ 'base-icon--clickable': props.clickable }"
    :style="iconStyle"
    @click="handleClick"
    @pointerdown="handlePointerDown"
  >
    <Icon :name="props.name" :size="props.size" :customize="customize" />
  </span>
</template>

<script setup lang="ts">
import { defaultFillTypeFromIconName, playActionEffectTap } from '~/shared/lib'

export interface IProps {
  name: string
  color?: string
  size?: string
  strokeWidth?: number
  fillType?: 'solid' | 'outline'
  rotate?: number
  clickable?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  color: 'var(--color-foreground-regular)',
  strokeWidth: 1.8,
  size: '24px',
})
const emit = defineEmits<{
  (e: 'click', value: Event): void
}>()

function handlePointerDown(event: PointerEvent) {
  if (!props.clickable) {
    return
  }

  playActionEffectTap(event.currentTarget as HTMLElement)
}

function handleClick(event: Event) {
  emit('click', event)
}

const resolvedFillType = computed((): 'solid' | 'outline' => {
  if (props.fillType !== undefined) {
    return props.fillType
  }
  return defaultFillTypeFromIconName(props.name)
})

function customize(content: string) {
  const strokeWidth = props.strokeWidth

  let modifiedContent = content
    .replace(/stroke-width="[^"]*"/g, `stroke-width="${strokeWidth}"`)
    .replace(/stroke="[^"]*"/g, `stroke="currentColor"`)

  switch (resolvedFillType.value) {
    case 'solid':
      modifiedContent = modifiedContent.replace(
        /fill="[^"]*"/g,
        `fill="currentColor"`,
      )
      break
    case 'outline':
      modifiedContent = modifiedContent.replace(/fill="[^"]*"/g, `fill="none"`)
      break
  }

  return modifiedContent.replace(/opacity="[^"]*"/g, `opacity="0.4"`)
}

const iconStyle = computed(() => ({
  color: props.color,
  transform: `rotate(${props.rotate || 0}deg)`,
  cursor: props.clickable ? 'pointer' : 'inherit',
}))
</script>

<style lang="scss">
.base-icon {
  transition: rotate 0.3s ease;
  display: inline-flex;
  color: v-bind(color);
  user-select: none;

  &--clickable {
    @include action-effect-transform;
  }
}
</style>
